import Link from "next/link";
import Image from "next/image";
import { useEffect, useState, useCallback } from "react";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import { trackEvent } from "@/lib/analytics";
import type MuxPlayerElement from "@mux/mux-player";
import { clsx } from "clsx";
import { useAuth } from "@clerk/nextjs";
import { useMembership } from "@/lib/hooks/useMembership";
import { StoryblokComponent, storyblokEditable } from "@storyblok/react/rsc";
import { renderRichText } from "@storyblok/react";
import { ShieldQuestion } from "lucide-react";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Alert, AlertTitle } from "@/components/ui/alert";

import { Chapters } from "@/components/layout/Chapters";
import { Headline } from "@/components/typography/Headline";
import { Subline } from "@/components/typography/Subline";
import { CopyText } from "@/components/typography/CopyText";
import { RichText } from "@/components/typography/RichText";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Transcript } from "@/components/media/Transcript";
import { VideoSkeleton } from "@/components/layout/Skeleton";
import type { CuePoint } from "@/components/media/TranscriptCuePoint";
import Ad from "@/components/storyblok/Ad";
import { MemberLockIcon } from "@/components/icons/MemberLockIcon";

import { Rating } from "@/components/layout/Rating";
import type {
  LessonStoryblok,
  CourseStoryblok,
  AdStoryblok,
} from "@/component-types-sb";

import {
  getLessonMetrics,
  getLessonRatings,
  markLessonAsCompleted,
  updateLessonTime,
  updateLessonRating,
} from "@/app/(web)/courses/actions";
import type { LessonMetric } from "@/lib/schema/course";
import { useConsent } from "@/lib/hooks/useConsent";
import { setReturnIntent } from "@/lib/auth/return-intent";

const MuxPlayer = dynamic(() => import("@mux/mux-player-react"), {
  ssr: false,
  loading: () => <VideoSkeleton className="w-full h-full" />,
});

type LessonProps = {
  blok: LessonStoryblok;
  uuid: string;
  playbackToken?: string;
  thumbnailToken?: string;
  course?: CourseStoryblok;
  completedLessons?: string[];
  showVideo?: boolean;
};

const Lesson = ({
  blok,
  uuid,
  playbackToken,
  thumbnailToken,
  course,
  completedLessons = [],
  showVideo = false,
}: LessonProps) => {
  const { userId } = useAuth();
  const { isMember } = useMembership();
  const accessDenied = (blok as any)._access_denied === true;
  const pathname = usePathname();
  const [currentTime, setCurrentTime] = useState(0);
  const [metric, setMetric] = useState<LessonMetric | null>();
  const [rating, setRating] = useState<number | null>(0);
  const [activeCuePoint, setActiveCuePoint] = useState<CuePoint | undefined>();

  const [showAd, setShowAd] = useState(false);
  const [adShown, setAdShown] = useState(false);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    trackEvent("content_viewed", {
      type: "lesson",
      title: blok.title,
      slug: pathname,
    });
  }, [blok.title, pathname]);

  const { statistics } = useConsent();
  const startTime = Number(blok.ad_start_time) || 30;
  const duration = Number(blok.ad_duration) || 10;
  const ad = blok.ad as AdStoryblok | undefined;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getLessonMetrics(uuid);
        setMetric(data);
      } catch (err) {
        console.error(err);
      }
      // setCurrentTime(data?.time || 0);
    };
    fetchData();
  }, [uuid, setMetric]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getLessonRatings(uuid);
        setRating(data?.rating || 0);
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, [uuid, setRating]);

  // Countdown ad view to zero
  useEffect(() => {
    if (seconds > 0) {
      const intervalId = setInterval(() => {
        setSeconds((prevSeconds) => prevSeconds - 1);
      }, 1000);

      return () => clearInterval(intervalId);
    }
  }, [seconds, setSeconds]);

  useEffect(() => {
    if (showAd) {
      setSeconds(duration);
    } else {
      setSeconds(0);
    }
  }, [showAd, setSeconds, duration]);

  const cuePoints: CuePoint[] =
    blok.cue_points?.map(({ name, time }) => ({
      time: Number(time),
      value: name,
    })) || [];

  const addCuePointsToPlayer = (playerEl: MuxPlayerElement) => {
    playerEl.addCuePoints(cuePoints);
  };

  const saveWatchProgress = async (playerEl: MuxPlayerElement) => {
    // saving time in full seconds
    const seconds = Math.floor(playerEl.currentTime);
    await updateLessonTime(uuid, seconds);
  };

  const onTimeUpdate = useCallback(
    (e: any) => {
      const { currentTime, paused } = e.target;
      const currentSeconds = Math.floor(currentTime);
      const endTime = startTime + duration;

      if (
        ad &&
        !isMember &&
        !paused &&
        !showAd &&
        !adShown &&
        currentSeconds >= startTime &&
        currentSeconds <= endTime
      ) {
        setShowAd(true);

        // Hide add after some time
        setTimeout(() => {
          setShowAd(false);
          setAdShown(true);
        }, duration * 1000);
      }
    },
    [ad, isMember, duration, startTime, showAd, setShowAd, adShown, setAdShown]
  );

  const onEnded = async () => {
    if (userId && !completedLessons?.includes(uuid)) {
      await markLessonAsCompleted(uuid);
    }
  };

  const saveRating = async (_event: any, value: number) => {
    await updateLessonRating(uuid, value);
    const data = await getLessonRatings(uuid);
    setRating(data?.rating || 0);
  };

  return (
    <article
      className="pt-12 max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 text-black-100"
      {...storyblokEditable(blok)}
    >
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href={`/${course?.full_slug}`}>
              {course?.name}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{blok.title}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex md:flex-row flex-col gap-4 md:divide-x-2 divide-sand-50">
        <div className="basis-3/4 flex flex-col gap-4">
          {userId && (
            <Transcript
              activeCuePoint={activeCuePoint}
              cuePoints={cuePoints}
              onCuePointSelected={(cuePoint) =>
                setCurrentTime(cuePoint.time ?? 0)
              }
            />
          )}

          {showVideo ? (
            <div className="relative">
              {ad && !isMember && (
                <div
                  className={clsx(
                    "flex flex-col items-center absolute w-full h-full z-10 bg-sand-100 p-2",
                    {
                      ["hidden"]: !showAd,
                    }
                  )}
                >
                  <Ad
                    blok={ad}
                    label={`In ${seconds} Sekunden geht es weiter...`}
                    className="overflow-hidden"
                  />
                </div>
              )}

              <MuxPlayer
                streamType="on-demand"
                currentTime={currentTime}
                // startTime={metric?.time || 0}
                playbackId={blok.playback_id}
                {...(blok.thumbnail?.filename && { poster: blok.thumbnail.filename })}
                {...(blok.thumbnail_time && { thumbnailTime: blok.thumbnail_time })}
                tokens={{
                  playback: playbackToken,
                  thumbnail: thumbnailToken,
                }}
                metadataVideoTitle={blok.title}
                metadataViewerUserId={userId || undefined}
                preload="auto"
                paused={showAd}
                onPause={({ target }) => {
                  saveWatchProgress(target as MuxPlayerElement);
                }}
                onTimeUpdate={onTimeUpdate}
                onEnded={onEnded}
                onLoadedMetadata={({ target }) => {
                  addCuePointsToPlayer(target as MuxPlayerElement);
                }}
                onCuePointChange={({ target }) => {
                  setActiveCuePoint(
                    (target as MuxPlayerElement).activeCuePoint
                  );
                }}
                onCuePointsChange={({ target }) => {
                  setActiveCuePoint(
                    (target as MuxPlayerElement).activeCuePoint
                  );
                }}
                className="aspect-video"
                disableCookies={!statistics}
                accentColor="#D3C4AC"
              />
            </div>
          ) : accessDenied ? (
            <div className="relative aspect-video mb-4 rounded-2xl overflow-hidden bg-sky-50">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4 text-center px-6 max-w-md">
                  <MemberLockIcon locked />
                  <h3 className="text-headline-md text-black">
                    Diese Lektion ist exklusiv für Mitglieder
                  </h3>
                  <Link
                    href={userId ? "/dashboard" : `/login?redirect=${encodeURIComponent(pathname)}`}
                    onClick={() => { if (!userId) setReturnIntent(pathname, "lesson_access"); }}
                    className="inline-flex items-center justify-center rounded-full bg-black text-white px-6 py-3 text-button hover:opacity-90 transition-opacity"
                  >
                    Mitglied werden
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <>
              <Alert>
                <AlertTitle>
                  Im kostenlosen Tarif kannst Du nur ein Kurs-Video pro Tag
                  anschauen. Wir arbeiten aktuell an Premium-Tarifen.
                </AlertTitle>
              </Alert>

              <Image
                src={blok.image?.filename || "/d.svg"}
                alt={blok.image?.alt || ""}
                sizes="(max-width: 768px) 100vw, 30vw"
                width={1080}
                height={608}
                className="w-full h-auto mb-4"
              />
            </>
          )}

          <div className="flex justify-between">
            <Subline>{blok.chapter}</Subline>
            <Rating
              value={rating}
              showText
              onClick={userId ? saveRating : undefined}
            />
          </div>

          <Headline as="h2" size="tall" className="mb-4">
            {blok.title}
          </Headline>
          <CopyText className="max-w-3xl tracking-wide">
            {blok.description}
          </CopyText>

          <div className={clsx(accessDenied && "relative overflow-hidden")}>
            <RichText
              html={renderRichText(blok.exercise)}
              className={clsx("max-w-3xl tracking-wide", {
                ["line-clamp-3"]: !userId && !accessDenied,
                ["line-clamp-2"]: accessDenied,
              })}
            />
            {accessDenied && (
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-sand-10 to-transparent pointer-events-none" />
            )}
          </div>

          {blok.transcript && (
            <>
              <Separator className="my-4" />
              <h4 className="text-sm font-medium leading-none mb-4">
                Transcript
              </h4>

              <section className={clsx("bg-gray-50 rounded-md p-6", accessDenied ? "relative overflow-hidden" : "overflow-y-auto h-96")}>
                <blockquote className="italic font-light text-gray-900">
                  <svg
                    className="w-8 h-8 text-gray-400 mb-4"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 18 14"
                  >
                    <path d="M6 0H2a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h4v1a3 3 0 0 1-3 3H2a1 1 0 0 0 0 2h1a5.006 5.006 0 0 0 5-5V2a2 2 0 0 0-2-2Zm10 0h-4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h4v1a3 3 0 0 1-3 3h-1a1 1 0 0 0 0 2h1a5.006 5.006 0 0 0 5-5V2a2 2 0 0 0-2-2Z" />
                  </svg>
                  <p className={clsx(accessDenied && "line-clamp-3")}>{blok.transcript}</p>
                </blockquote>
                {accessDenied && (
                  <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-50 to-transparent pointer-events-none" />
                )}
              </section>
            </>
          )}
        </div>

        <aside className="px-4 basis-1/4 flex flex-col">
          <Chapters
            items={course?.content.lessons}
            completed={completedLessons}
            chapter={blok.chapter}
            uuid={uuid}
            isMember={isMember}
          />

          <div className="mt-8 flex flex-col gap-4">
            {userId && !accessDenied && blok.evaluation && (
              <Link
                href={`/${(blok.evaluation as any).full_slug}`}
              >
                <Button className="rounded-full px-6 py-4">
                  <ShieldQuestion className="mr-2" />
                  {(blok.evaluation as any).content.title}
                </Button>
              </Link>
            )}
          </div>

          {!accessDenied && (
            <div className="mt-8 flex flex-col gap-4">
              {blok.files?.map((nestedBlok: any) => (
                <StoryblokComponent blok={nestedBlok} key={nestedBlok._uid} />
              ))}
            </div>
          )}
        </aside>
      </div>
    </article>
  );
};

export default Lesson;
